import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiShield as Shield, FiAlertTriangle as AlertTriangle, FiCheckCircle as CheckCircle, FiXCircle as XCircle, FiTrendingUp as TrendingUp, FiFilter as Filter, FiTrash2 as TrashIcon, FiSquare as Square, FiCheckSquare as CheckSquare, FiFileText as FileText } from 'react-icons/fi';
import { getSafetyViolations, getSafetyStats, bulkDeleteSafetyViolations, bulkAssignRunProject, bulkAssignRunWorkload } from '../../../../services/aiOperationsApi';
import { useProject } from '../../../../contexts/ProjectContext';
import { useFilter } from '../../../../contexts/FilterContext';
import ProjectAssignmentSelect from '../../../../components/ProjectAssignmentSelect';
import WorkloadAssignmentSelect from '../../../../components/WorkloadAssignmentSelect';
import { useWorkloads } from '../../../../features/hub/workloads/hooks/useWorkloads';

interface SafetyViolation {
  id: string;
  severity: string;
  violation_type: string;
  type?: string;
  description?: string;
  message?: string;
  action_taken?: string;
  detected_at?: string;
  agent_id?: string;
  agent_name?: string;
  session_id?: string;
  timestamp?: string;
  created_at?: string;
  run_id?: string;
  project_id?: string;
  workload_id?: string;
  [key: string]: unknown;
}

interface SafetyStats {
  total_violations: number;
  high_severity_count: number;
  medium_severity_count: number;
  blocked_count: number;
  compliance_rate?: number;
  trend_percentage?: number;
  [key: string]: unknown;
}

interface PaginationProps {
  currentPage: number;
  totalPages: number;
  onPageChange: (updater: (p: number) => number) => void;
}

const Pagination = ({ currentPage, totalPages, onPageChange }: PaginationProps) => {
  if (totalPages <= 1) return null;

  return (
    <div className="flex items-center justify-center gap-2 mt-6 pb-6">
      <button
        onClick={() => onPageChange(p => Math.max(1, p - 1))}
        disabled={currentPage === 1}
        className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md
                 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300
                 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed
                 transition-colors"
      >
        Previous
      </button>
      <span className="text-gray-700 dark:text-gray-300">
        Page {currentPage} of {totalPages}
      </span>
      <button
        onClick={() => onPageChange(p => Math.min(totalPages, p + 1))}
        disabled={currentPage === totalPages}
        className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md
                 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300
                 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed
                 transition-colors"
      >
        Next
      </button>
    </div>
  );
};

const SafetyPage = () => {
  const { selectedProjectId, projects } = useProject();
  const { workloads } = useWorkloads();
  const { filterType, selectedId: filterId } = useFilter();
  const navigate = useNavigate();
  const [violations, setViolations] = useState<SafetyViolation[]>([]);
  const [stats, setStats] = useState<SafetyStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [filters, setFilters] = useState({
    severity: '',
    type: '',
    days: 7
  });
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  useEffect(() => {
    loadData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, filters, selectedProjectId, filterType, filterId]);

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      // Load violations
      const violationsParams = {
        page: currentPage,
        limit: 50,
        ...Object.fromEntries(
          Object.entries(filters).filter(([k, v]) => v !== '' && k !== 'days')
        )
      };

      const violationsData = await getSafetyViolations(violationsParams);
      setViolations(violationsData.data || violationsData.violations || []);
      setTotalPages(violationsData.meta?.totalPages || 1);

      // Load stats
      const statsParams = { days: filters.days };
      const statsData = await getSafetyStats(statsParams);
      setStats(statsData);
    } catch (err: unknown) {
      console.error('Error loading safety data:', err);
      // Provide user-friendly error messages
      const errMessage = err instanceof Error ? err.message : String(err);
      let errorMessage = 'Unable to load safety data.';
      if (errMessage?.includes('Failed to fetch') || errMessage?.includes('NetworkError')) {
        errorMessage = 'Unable to connect to the safety service. Please check your connection and try again.';
      } else if (errMessage?.includes('CORS') || (err instanceof Error && err.name === 'TypeError')) {
        errorMessage = 'Unable to connect to the safety service. Please try again later.';
      } else if (errMessage) {
        errorMessage = errMessage;
      }
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const formatTimestamp = (timestamp: string | null | undefined) => {
    if (!timestamp) return 'N/A';
    return new Date(timestamp).toLocaleString();
  };

  const formatNumber = (num: number | null | undefined) => {
    if (!num && num !== 0) return '0';
    return num.toLocaleString();
  };

  const getSeverityColor = (severity: string | null | undefined) => {
    switch (severity?.toLowerCase()) {
      case 'high':
      case 'critical':
        return 'bg-red-100 dark:bg-red-900/50 text-red-800 dark:text-red-300';
      case 'medium':
      case 'warning':
        return 'bg-yellow-100 dark:bg-yellow-900/50 text-yellow-800 dark:text-yellow-300';
      case 'low':
      case 'info':
        return 'bg-blue-100 dark:bg-blue-900/50 text-blue-800 dark:text-blue-300';
      default:
        return 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300';
    }
  };

  const getSeverityIcon = (severity: string | null | undefined) => {
    switch (severity?.toLowerCase()) {
      case 'high':
      case 'critical':
        return <XCircle className="w-4 h-4" />;
      case 'medium':
      case 'warning':
        return <AlertTriangle className="w-4 h-4" />;
      case 'low':
      case 'info':
        return <CheckCircle className="w-4 h-4" />;
      default:
        return <Shield className="w-4 h-4" />;
    }
  };

  const getActionTakenColor = (action: string | null | undefined) => {
    switch (action?.toLowerCase()) {
      case 'blocked':
        return 'bg-red-100 dark:bg-red-900/50 text-red-800 dark:text-red-300';
      case 'filtered':
      case 'redacted':
        return 'bg-yellow-100 dark:bg-yellow-900/50 text-yellow-800 dark:text-yellow-300';
      case 'throttled':
        return 'bg-orange-100 dark:bg-orange-900/50 text-orange-800 dark:text-orange-300';
      case 'logged':
      case 'flagged':
        return 'bg-blue-100 dark:bg-blue-900/50 text-blue-800 dark:text-blue-300';
      default:
        return 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300';
    }
  };

  const handleRowClick = (violation: SafetyViolation) => {
    navigate(`/ai-operations/safety/${violation.id}`);
  };

  const toggleSelect = useCallback((id: string) => {
    setSelectedIds((prev) => { const next = new Set(prev); if (next.has(id)) next.delete(id); else next.add(id); return next; });
  }, []);

  const toggleSelectAll = useCallback(() => {
    setSelectedIds((prev) => prev.size === violations.length ? new Set() : new Set(violations.map((v) => v.id)));
  }, [violations]);

  const handleBulkDelete = async () => {
    if (selectedIds.size === 0) return;
    try {
      await bulkDeleteSafetyViolations(Array.from(selectedIds));
      setSelectedIds(new Set());
      loadData();
    } catch (err: unknown) {
      console.error('Error deleting safety violations:', err);
    }
  };

  const getProjectName = (projectId?: string) => (
    projectId ? (projects.find((project) => project.id === projectId)?.name ?? null) : null
  );

  const getWorkloadName = (workloadId?: string) => (
    workloadId ? (workloads.find((w) => w.id === workloadId)?.name ?? null) : null
  );

  const handleBulkAssignProject = async (projectId: string | null) => {
    const runIds = Array.from(
      new Set(
        violations
          .filter((violation) => selectedIds.has(violation.id))
          .map((violation) => violation.run_id)
          .filter((runId): runId is string => Boolean(runId))
      )
    );

    if (runIds.length === 0) return;

    await bulkAssignRunProject(runIds, projectId);
    setViolations((prev) => prev.map((violation) => (
      selectedIds.has(violation.id) ? { ...violation, project_id: projectId ?? undefined } : violation
    )));
    setSelectedIds(new Set());
  };

  const handleBulkAssignWorkload = async (workloadId: string | null) => {
    const runIds = Array.from(
      new Set(
        violations
          .filter((violation) => selectedIds.has(violation.id))
          .map((violation) => violation.run_id)
          .filter((runId): runId is string => Boolean(runId))
      )
    );

    if (runIds.length === 0) return;

    await bulkAssignRunWorkload(runIds, workloadId);
    setSelectedIds(new Set());
    loadData();
  };


  if (loading && !stats) {
    return (
      <div className="mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  if (error && !stats) {
    return (
      <div className="mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-6">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-6 h-6 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <h3 className="text-lg font-medium text-red-800 dark:text-red-300 mb-1">
                Failed to Load Safety Data
              </h3>
              <p className="text-red-700 dark:text-red-400 mb-4">
                {error}
              </p>
              <button
                onClick={loadData}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md
                         text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500
                         transition-colors"
              >
                Retry
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white transition-colors">
          Safety & Compliance
        </h1>
        <p className="mt-2 text-gray-600 dark:text-gray-400 transition-colors">
          Monitor safety violations, guardrails, and compliance metrics across agent executions
        </p>
      </div>

      {/* Summary Cards */}
      {stats && (
        <div className="mb-8 grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6 transition-colors">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">Total Violations</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
                  {formatNumber(stats.total_violations || 0)}
                </p>
              </div>
              <Shield className="w-8 h-8 text-blue-600" />
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6 transition-colors">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">High Severity</p>
                <p className="text-2xl font-bold text-red-600 dark:text-red-400 mt-1">
                  {formatNumber(stats.high_severity_count || 0)}
                </p>
              </div>
              <XCircle className="w-8 h-8 text-red-600" />
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6 transition-colors">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">Medium Severity</p>
                <p className="text-2xl font-bold text-yellow-600 dark:text-yellow-400 mt-1">
                  {formatNumber(stats.medium_severity_count || 0)}
                </p>
              </div>
              <AlertTriangle className="w-8 h-8 text-yellow-600" />
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6 transition-colors">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">Compliance Rate</p>
                <p className="text-2xl font-bold text-green-600 dark:text-green-400 mt-1">
                  {stats.compliance_rate ? `${stats.compliance_rate.toFixed(1)}%` : 'N/A'}
                </p>
              </div>
              <TrendingUp className="w-8 h-8 text-green-600" />
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="mb-6 bg-white dark:bg-gray-800 rounded-lg shadow-sm p-4 transition-colors">
        <div className="flex items-center gap-2 mb-4">
          <button
            onClick={toggleSelectAll}
            className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors text-gray-600 dark:text-gray-400"
            title={selectedIds.size === violations.length && violations.length > 0 ? 'Deselect all' : 'Select all'}
          >
            {selectedIds.size === violations.length && violations.length > 0 ? (
              <CheckSquare className="w-5 h-5 text-blue-600 dark:text-blue-400" />
            ) : (
              <Square className="w-5 h-5" />
            )}
          </button>
          <Filter className="w-5 h-5 text-gray-600 dark:text-gray-400" />
          <h3 className="text-sm font-medium text-gray-900 dark:text-white">Filters</h3>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label htmlFor="severity" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Severity
            </label>
            <select id="severity"
              value={filters.severity}
              onChange={(e) => setFilters({ ...filters, severity: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md
                       bg-white dark:bg-gray-700 text-gray-900 dark:text-white
                       focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">All Severities</option>
              <option value="high">High</option>
              <option value="medium">Medium</option>
              <option value="low">Low</option>
            </select>
          </div>

          <div>
            <label htmlFor="violation-type" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Violation Type
            </label>
            <select id="violation-type"
              value={filters.type}
              onChange={(e) => setFilters({ ...filters, type: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md
                       bg-white dark:bg-gray-700 text-gray-900 dark:text-white
                       focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">All Types</option>
              <option value="content">Content Violation</option>
              <option value="behavior">Behavior Violation</option>
              <option value="security">Security Violation</option>
              <option value="compliance">Compliance Violation</option>
            </select>
          </div>

          <div>
            <label htmlFor="time-range" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Time Range
            </label>
            <select id="time-range"
              value={filters.days}
              onChange={(e) => setFilters({ ...filters, days: Number(e.target.value) })}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md
                       bg-white dark:bg-gray-700 text-gray-900 dark:text-white
                       focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value={1}>Last 24 hours</option>
              <option value={7}>Last 7 days</option>
              <option value={30}>Last 30 days</option>
              <option value={90}>Last 90 days</option>
            </select>
          </div>
        </div>
      </div>

      {/* Bulk Action Bar */}
      {selectedIds.size > 0 && (
        <div className="mb-4 flex items-center justify-between rounded-lg bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 px-4 py-3">
          <div className="flex items-center gap-4">
            <span className="text-sm font-medium text-blue-800 dark:text-blue-200">{selectedIds.size} selected</span>
            <button type="button" onClick={() => setSelectedIds(new Set())} className="text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200">Clear selection</button>
          </div>
          <div className="flex items-center gap-2">
            <ProjectAssignmentSelect
              bulk
              currentProjectId={null}
              onAssign={handleBulkAssignProject}
              size="sm"
            />
            <WorkloadAssignmentSelect
              bulk
              onAssign={handleBulkAssignWorkload}
              size="sm"
            />
            <button type="button" onClick={handleBulkDelete} className="inline-flex items-center gap-1.5 rounded-md bg-red-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-red-700 transition-colors">
              <TrashIcon className="w-4 h-4" />Delete Selected
            </button>
          </div>
        </div>
      )}

      {/* Violations Table */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm transition-colors overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
            Safety Violations ({violations.length})
          </h2>
        </div>

        {violations.length === 0 ? (
          <div className="p-12 text-center">
            <Shield className="w-12 h-12 text-green-400 mx-auto mb-3" />
            <p className="text-gray-600 dark:text-gray-400 mb-2">
              No safety violations detected
            </p>
            <p className="text-sm text-gray-500 dark:text-gray-500">
              All agent executions are compliant with safety guardrails
            </p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-900/50">
                  <tr>
                    <th className="px-3 py-3 w-10">
                      <button
                        onClick={toggleSelectAll}
                        className="p-0.5 rounded hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors text-gray-500 dark:text-gray-400"
                      >
                        {selectedIds.size === violations.length && violations.length > 0 ? (
                          <CheckSquare className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                        ) : (
                          <Square className="w-4 h-4" />
                        )}
                      </button>
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Severity
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Type
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Description
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Agent
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Action
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Timestamp
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                  {violations.map((violation, index) => (
                    <tr
                      key={violation.id || index}
                      onClick={() => handleRowClick(violation)} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') handleRowClick(violation); }} role="row" tabIndex={0}
                      className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors cursor-pointer"
                    >
                      <td className="px-3 py-4 w-10" onClick={(e) => e.stopPropagation()} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') e.stopPropagation(); }} role="presentation">
                        <button
                          onClick={() => toggleSelect(violation.id)}
                          className="p-0.5 rounded hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors text-gray-500 dark:text-gray-400"
                        >
                          {selectedIds.has(violation.id) ? (
                            <CheckSquare className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                          ) : (
                            <Square className="w-4 h-4" />
                          )}
                        </button>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium ${getSeverityColor(violation.severity)}`}>
                          {getSeverityIcon(violation.severity)}
                          {violation.severity || 'Unknown'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="text-sm text-gray-900 dark:text-gray-300">
                          {violation.violation_type || violation.type || 'N/A'}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                          <p className="text-sm text-gray-900 dark:text-gray-300 max-w-md truncate">
                            {violation.description || violation.message || 'No description available'}
                          </p>
                          {getProjectName(violation.project_id) && (
                            <span className="px-2 py-0.5 text-xs font-medium bg-green-100 dark:bg-green-900/50 text-green-800 dark:text-green-300 rounded">
                              {getProjectName(violation.project_id)}
                            </span>
                          )}
                          {getWorkloadName(violation.workload_id) && (
                            <span className="px-2 py-0.5 text-xs font-medium bg-purple-100 dark:bg-purple-900/50 text-purple-800 dark:text-purple-300 rounded">
                              {getWorkloadName(violation.workload_id)}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="text-sm text-gray-900 dark:text-gray-300">
                          {violation.agent_name || violation.agent_id || 'Unknown'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {violation.action_taken && (
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getActionTakenColor(violation.action_taken)}`}>
                            {violation.action_taken}
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-300">
                        {formatTimestamp(violation.timestamp || violation.created_at)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-right">
                        <div className="flex items-center justify-end gap-3">
                          {violation.run_id && (
                            <button
                              onClick={(e) => { e.stopPropagation(); navigate(`/ai-operations/traces/${violation.run_id}`); }}
                              className="inline-flex items-center gap-1 text-indigo-600 hover:text-indigo-900 dark:text-indigo-400 dark:hover:text-indigo-300"
                              title="View Trace Details"
                            >
                              <FileText className="w-3.5 h-3.5" />
                              Trace
                            </button>
                          )}
                          <span className="text-blue-600 hover:text-blue-900 dark:text-blue-400 dark:hover:text-blue-300">
                            View &gt;
                          </span>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} />
          </>
        )}
      </div>
    </div>
  );
};

export default SafetyPage;
